import { type FC } from 'react';

import { Badge, Flex, Grid, SectionHeader } from '@highereducation/hen-ui';

import { SponsoredResultsPopover } from '../SponsoredResultsPopover';
import { Card } from '../Card';
import type { ResultListProps } from './types';
import { EventingLocation } from '../../types/enums';
import { useResultsPageConfigOptional } from '../../providers/ResultsPageConfigProvider';

/**
 * Single unified results-list. Replaces the legacy
 * `ResultList/{UnderGradResultsList,GradResultsList}.tsx` pair. Uses
 * the UG layout (2-col grid + optional header + sponsored-results
 * popover) and forwards every Card prop the legacy Grad list passed so
 * the comparison checkbox and full ProductFlags render correctly.
 * Section chrome uses hen-ui `SectionHeader` + `Badge` (RP-2.3).
 */
const ResultList: FC<ResultListProps> = ({
  results,
  header,
  subheader,
  showSponsoredResultsPopover = true,
  showComparisonCheckbox = true,
  onProgramClick,
  onNextStepsClick,
  submittedResults = [],
  showPopupExperience = false,
  lead,
  partnerIdentifiers,
}) => {
  const resultsPageConfig = useResultsPageConfigOptional();
  const disclosureCopy = resultsPageConfig?.getRecommenderModalCopy();
  const trimmedHeader = header?.trim() ?? '';
  const trimmedSubheader = subheader?.trim() ?? '';
  const hasHeadingCopy = Boolean(trimmedHeader) || Boolean(trimmedSubheader);
  const sponsoredTotal = results?.length ?? 0;
  const sponsoredBadgeLabel =
    sponsoredTotal === 1
      ? '1 sponsored result'
      : `${sponsoredTotal} sponsored results`;
  const showTopChrome = hasHeadingCopy || showSponsoredResultsPopover;

  return (
    <Flex
      direction='column'
      gap='none'
      width='full'
      className='not-prose mx-auto mb-6'
    >
      {showTopChrome && (
        <Flex
          className='mb-6'
          direction='column'
          direction-md='row'
          justify={hasHeadingCopy ? 'between' : 'end'}
          align='start'
          gap='md'
        >
          {hasHeadingCopy && (
            <Flex direction='column' gap='none' className='min-w-0 flex-1'>
              <SectionHeader
                size='h2'
                title={trimmedHeader || trimmedSubheader}
                subtext={
                  trimmedHeader && trimmedSubheader
                    ? trimmedSubheader
                    : undefined
                }
              />
            </Flex>
          )}
          {showSponsoredResultsPopover && (
            <Flex
              direction='row'
              align='center'
              gap='sm'
              wrap='wrap'
              className={hasHeadingCopy ? undefined : 'w-full md:w-auto'}
            >
              <Badge variant='light' size='sm'>
                {sponsoredBadgeLabel}
              </Badge>
              <SponsoredResultsPopover
                spanText='Learn More About Our Partners'
                disclosureTitle={disclosureCopy?.disclosureTitle}
                disclosureContent={disclosureCopy?.disclosureContent}
              />
            </Flex>
          )}
        </Flex>
      )}
      <Grid columns={1} columns-md={2} gap='lg' className='pb-6 [&>*]:min-w-0'>
        {results?.map((result, index) => {
          const isProgramSubmitted = submittedResults.some(
            (submission) => submission.programId === result.programId,
          );

          return (
            <Card
              onProductClick={(corr) => onProgramClick({ ...result, ...corr })}
              onNextStepsClick={
                onNextStepsClick
                  ? (corr) => onNextStepsClick({ ...result, ...corr })
                  : undefined
              }
              key={`${result.programId}-${result.schoolName}-${result.programName}`}
              schoolName={result.schoolName}
              schoolId={result.schoolId}
              schoolSlug={result.schoolSlug}
              programName={result.programName}
              city={result.city}
              state={result.state}
              snippet={result.snippet}
              levelOfInstitution={result.levelOfInstitution}
              accreditator={result.accreditator}
              percentOnlineEnrollment={
                result.percentOnlineEnrollment ?? undefined
              }
              isOnline={result.isOnline}
              isPublic={result.isPublic}
              forProfit={result.forProfit}
              notForProfit={result.notForProfit}
              audience={result.audience}
              position={index + 1}
              showComparisonCheckbox={showComparisonCheckbox}
              isProgramSubmitted={isProgramSubmitted}
              applicationCtaLabel={result.applicationCtaLabel || ''}
              applicationCtaURL={result.applicationCtaURL || ''}
              applicationCtaFieldMap={result.applicationCtaFieldMap}
              applicationCtaStaticParams={result.applicationCtaStaticParams}
              applicationCtaMissingValue={result.applicationCtaMissingValue}
              lead={lead}
              partnerIdentifiers={partnerIdentifiers}
              location={EventingLocation.PAGE}
              degreeSlug={result.degreeSlug}
              categorySlug={result.categorySlug}
              subjectSlug={result.subjectSlug}
              capId={result.capId}
              programId={result.programId}
              offeringId={result.offeringId}
              offeringUuid={result.offeringUuid}
              organizationId={result.organizationId}
              tcpa={result.tcpa}
              listId={result.listId}
              isUserRequested={result.isUserRequested}
              showPopupExperience={showPopupExperience}
            />
          );
        })}
      </Grid>
    </Flex>
  );
};

export default ResultList;
